import type { GameState } from '../../game-core/src/types.ts';
import type { SaveRepository } from './save-repository.ts';

export interface AutosaveOptions {
  intervalMs?: number;
  onError?: (error: unknown) => void;
}

export class AutosaveController {
  private timer: ReturnType<typeof setInterval> | null = null;
  private detachLifecycle: (() => void) | null = null;
  private readonly intervalMs: number;
  private readonly onError: (error: unknown) => void;
  private readonly repository: SaveRepository;
  private readonly getState: () => GameState;

  constructor(
    repository: SaveRepository,
    getState: () => GameState,
    options: AutosaveOptions = {}
  ) {
    this.repository = repository;
    this.getState = getState;
    this.intervalMs = options.intervalMs ?? 15_000;
    this.onError = options.onError ?? (() => undefined);
  }

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => void this.flush(), this.intervalMs);
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      const onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') void this.flush();
      };
      const onPageHide = () => void this.flush();
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('pagehide', onPageHide);
      this.detachLifecycle = () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', onPageHide);
      };
    }
  }

  stop(): void {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
    this.detachLifecycle?.();
    this.detachLifecycle = null;
  }

  async flush(): Promise<void> {
    try {
      await this.repository.save(this.getState());
    } catch (error) {
      this.onError(error);
    }
  }
}
